/* ----------------- React --------------- */
import { useEffect, useState, useTransition } from "react";

/* ----------------- Constants --------------- */
import { ENDPOINT_HINT } from "@constants/urls";
import { getAuthToken } from "@helpers/auth-provider";
import { authHeaders } from "@memorang/ui/contexts/AuthTokenContext";

/* ----------------- Features --------------- */
import { SESSION_EVENTS } from "../constants";
import useSessionStore from "./useSessionStore";

import i18n from "@helpers/lib/i18n";
/* ----------------- Helpers --------------- */
import type { BaseItemSchema } from "@memorang/types";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import { getPlatform } from "@memorang/helpers/platform";
import type { Hint } from "@memorang/types";
/* ----------------- React Query --------------- */
import { useMutation } from "@tanstack/react-query";
import { fetch as expoFetch } from "expo/fetch";
import { v4 as uuidv4 } from "uuid";
import { getParentStem } from "./useAIFeedback";

export const useHint = ({
	item,
	type = "item",
	selectedChoice,
	selectedChoiceId,
}: {
	item: BaseItemSchema;
	type?: "item" | "choice";
	selectedChoice?: string;
	selectedChoiceId?: string;
}) => {
	const { id: itemId, metadata, content, tags } = item;

	const choices = item.response.type === "choice" ? item.response.choices : [];
	const stem = content?.stem?.text;
	const topics = metadata?.topics || tags?.map((tag) => tag.label);

	const appId = useAppStore((store) => store.app.id);

	const parentStem = getParentStem(item);

	const {
		currentExamContext: { examId },
	} = useExamContext();

	const [hints, setHints] = useState<Hint[]>([]);
	const correctChoice = choices?.find((choice) => choice.isCorrect);
	const explanation = correctChoice ? correctChoice.explanation : "";
	const incorrectChoices = choices
		?.filter((choice) => !choice.isCorrect)
		.map((choice) => choice.text);

	const correctChoiceText = correctChoice?.text;
	const { viewerId: userId } = useAuthContext();
	const addSessionEvent = useSessionStore((state) => state.addSessionEvent);

	const platform = getPlatform();
	const [loading, setLoading] = useState(false);
	const [streaming, setStreaming] = useState(false);

	const [pending, startTransition] = useTransition();
	const updateHintsForItem = useSessionStore(
		(state) => state.updateHintsForItem,
	);

	const { mutate: getHint } = useMutation({
		mutationKey: selectedChoiceId
			? ["hint", itemId, selectedChoiceId]
			: ["hint", itemId],
		mutationFn: async () => {
			const params = {
				platform,
				itemId: typeof itemId === "string" ? itemId : itemId?.toString(),
				examId: `${examId}`,
				originalQuestion: {
					topics,
					explanation,
					stem,
					incorrectChoices,
					correctChoice: correctChoiceText,
					selectedChoice,
				},
				type,
				userId,
				appId,
				outputLanguage: i18n.language,
				extraDetails: parentStem,
			};
			setLoading(true);
			const token = await getAuthToken();
			const response = await expoFetch(ENDPOINT_HINT, {
				method: "POST",
				headers: {
					Accept: "text/event-stream",
					...authHeaders(token),
				},
				body: JSON.stringify(params),
			});
			const reader = response.body?.getReader();
			return reader;
		},
		onSuccess: (reader) => {
			const hintId = uuidv4();
			const hintIndex = hints.length;

			addSessionEvent(itemId!, {
				name: SESSION_EVENTS.HINT_REQUESTED,
				props: {
					hintId,
					hintIndex,
				},
			});

			setLoading(false);
			setStreaming(true);
			setHints((prevHints) => [
				...prevHints,
				{
					id: hintId,
					text: "",
				},
			]);
			const decoder = new TextDecoder("utf-8");
			let result = "";

			async function read() {
				if (reader) {
					const { done, value } = await reader.read();
					if (done) {
						setStreaming(false);
						return;
					}
					result += decoder.decode(value);
					startTransition(() => {
						setHints((prevHint) => {
							prevHint[prevHint.length - 1].text = result;
							return prevHint;
						});
					});
					await read();
				}
			}
			read();
		},
	});

	useEffect(() => {
		if (hints.length > 0 && !loading && !streaming && !pending) {
			updateHintsForItem(itemId!, hints);
		}
	}, [hints, loading, streaming, itemId, pending, updateHintsForItem]);

	return {
		hints,
		loading,
		streaming,
		getHint,
	};
};

export type UseHintReturnType = ReturnType<typeof useHint>;
